import { Slider } from '@mui/material';
import * as R from 'ramda';
import { useEffect, useState } from 'react';

import {
  selectIsPlayerSeeking,
  selectIsTrackLoaded,
  selectPlayerProgress,
  selectPlayerSeekToPosition,
  useAppSelector,
} from '@/store';
import { Chapter } from '@/types';

interface ProgressBarProps {
  chapter?: Chapter;
  setSeekPosition: (position: { position: number }) => void;
}

export const ProgressBar = ({ chapter, setSeekPosition }: ProgressBarProps) => {
  const [sliderValue, setSliderValue] = useState<number>();
  const [isSliding, setIsSliding] = useState(false);

  const playerProgress = useAppSelector(selectPlayerProgress);
  const seekToPosition = useAppSelector(selectPlayerSeekToPosition);
  const isPlayerSeeking = useAppSelector(selectIsPlayerSeeking);
  const isTrackLoaded = useAppSelector(selectIsTrackLoaded);

  const chapterDuration = chapter?.duration;

  useEffect(() => {
    if (
      !isSliding &&
      !isPlayerSeeking &&
      !R.isNil(playerProgress.position) &&
      sliderValue !== playerProgress.position
    ) {
      setSliderValue(playerProgress.position);
    }
  }, [isPlayerSeeking, playerProgress, sliderValue, isSliding]);

  const onSlide = (val: number) => {
    setSliderValue(val);
    setIsSliding(true);
  };

  const onSlidingComplete = (val: number) => {
    setSeekPosition({ position: val });
    setIsSliding(false);
  };

  const position =
    sliderValue ||
    (isPlayerSeeking ? seekToPosition : playerProgress.position) ||
    0;

  return (
    <Slider
      value={position}
      max={chapterDuration}
      aria-label="Seek position"
      onChange={(_e, value) => onSlide(value as number)}
      onChangeCommitted={(_e, value) => onSlidingComplete(value as number)}
      sx={{
        ...styles.root,
        '& .MuiSlider-thumb': styles.thumb,
        '& .MuiSlider-track': styles.track,
        '& .MuiSlider-rail': styles.rail,
      }}
      disabled={!isTrackLoaded}
    />
  );
};

const styles = {
  root: {
    height: 4,
    p: '10px 0 !important',
  },
  track: {
    color: 'secondary.active',
    height: 4,
  },
  rail: {
    color: 'white',
    height: 4,
  },
  thumb: {
    color: 'secondary.active',
    height: 12,
    width: 12,
  },
};
